// src/components/admin/organizers/OrganizerApplicationsList.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchOrganizerApplications,
  fetchOrganizerApplicationDetails,
  approveOrganizerApplication,
  rejectOrganizerApplication,
  suspendOrganizer,
  unsuspendOrganizer,
  fetchOrganizerEventsForAdmin,
  grantEditOverride,
  revokeEditOverride,
  clearAuditNav,
} from "@/store/slices/adminSlice";
import {
  Building2,
  CheckCircle, XCircle, Eye, Clock, Ban, Unlock, AlertTriangle,
  ChevronDown, ArrowUpDown, Pencil, ShieldCheck, ShieldOff, Calendar,
} from "lucide-react";
import Button from "@/components/common/Button";
import Badge from "@/components/common/Badge";
import Modal from "@/components/common/Modal";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import TextArea from "@/components/common/TextArea";
import TextInput from "@/components/common/TextInput";
import FilterPanel from "@/components/common/FilterPanel";
import Table from "@/components/common/Table";
import Pagination from "@/components/common/Pagination";
import EntityAuditHistory from "@/components/admin/audit/EntityAuditHistory";
import { toast } from "react-toastify";

// ── Option Constants ──────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "suspended", label: "Suspended" },
];

const SEARCH_FIELDS = [
  { value: "search_name", label: "Organization" },
  { value: "search_email", label: "Email" },
  { value: "search_city", label: "City" },
];

const SORT_OPTIONS = [
  { value: "recent", label: "Recent" },
  { value: "name", label: "Name" },
  { value: "city", label: "City" },
];

// ── InlineDropdown (matches UsersList pattern) ────────────────────
const InlineDropdown = ({
  options,
  value,
  onChange,
  className = "",
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);

  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (opt) => {
    onChange({ target: { value: opt.value } });
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className={`flex items-center justify-between gap-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors ${
          compact ? "px-2 py-1 text-sm" : "px-3 py-2 text-sm w-full"
        }`}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute z-60 mt-1 w-full min-w-max bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-150"
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((opt, i) => (
            <div
              key={opt.value}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(opt);
              }}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                value === opt.value
                  ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium"
                  : i === highlight
                    ? "bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const OrganizerApplicationsList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    organizerApplications,
    organizersPage,
    organizersTotalPages,
    organizersLimit,
    isLoadingOrganizers,
    isUpdating,
    currentOrganizerApplication,
  } = useSelector((state) => state.admin);

  // Modal / action state (unchanged from before)
  const [selectedOrganizer, setSelectedOrganizer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showUnsuspendConfirm, setShowUnsuspendConfirm] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendDuration, setSuspendDuration] = useState("");
  const [activeTab, setActiveTab] = useState("details");

  // Edit override state
  const [organizerEvents, setOrganizerEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [togglingEventId, setTogglingEventId] = useState(null);
  const [showEditSection, setShowEditSection] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    search: "",
    searchField: "search_name",
    status: "",
  });

  // Sort state
  const [sorting, setSorting] = useState({
    field: "recent",
    order: "desc",
  });

  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ─── Audit Log Deep-Link: auto-open details when ?id= is in URL ───
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const deepLinkId = params.get('id');
    if (deepLinkId) {
      navigate(location.pathname, { replace: true });
      dispatch(fetchOrganizerApplicationDetails(deepLinkId));
    }
    return () => { dispatch(clearAuditNav()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentOrganizerApplication && currentOrganizerApplication._id) {
      setSelectedOrganizer(currentOrganizerApplication);
      setShowDetailsModal(true);
    }
  }, [currentOrganizerApplication]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Build API params and fetch
  const fetchList = useCallback(
    (page = 1) => {
      const apiFilters = {
        page,
        limit: organizersLimit,
        status: filters.status || undefined,
        sort: sorting.field,
        order: sorting.order,
      };

      if (debouncedSearch.trim()) {
        apiFilters[filters.searchField] = debouncedSearch.trim();
      }

      dispatch(fetchOrganizerApplications(apiFilters));
    },
    [dispatch, organizersLimit, debouncedSearch, filters.searchField, filters.status, sorting]
  );

  useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  const handlePageChange = (newPage) => {
    fetchList(newPage);
  };

  // ── Handlers ──────────────────────────────────────────────────

  const handleSortToggle = (field) => {
    setSorting((prev) => ({
      field,
      order: prev.field === field && prev.order === "desc" ? "asc" : "desc",
    }));
  };

  const handleClearFilters = () => {
    setFilters({ search: "", searchField: "search_name", status: "" });
  };

  const hasActiveFilters =
    filters.search.trim() !== "" ||
    filters.status !== "" ||
    filters.searchField !== "search_name";

  const handleApprove = async () => {
    if (!selectedOrganizer) return;
    try {
      await dispatch(approveOrganizerApplication(selectedOrganizer._id)).unwrap();
      toast.success("Organizer approved successfully");
      setShowApproveConfirm(false);
      setSelectedOrganizer(null);
      fetchList(organizersPage);
    } catch (error) {
      toast.error(error.message || "Failed to approve organizer");
    }
  };

  const handleReject = async () => {
    if (!selectedOrganizer || !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    try {
      await dispatch(
        rejectOrganizerApplication({
          organizerId: selectedOrganizer._id,
          rejection_reason: rejectionReason,
        })
      ).unwrap();
      toast.success("Organizer rejected");
      setShowRejectModal(false);
      setRejectionReason("");
      setSelectedOrganizer(null);
      fetchList(organizersPage);
    } catch (error) {
      toast.error(error.message || "Failed to reject organizer");
    }
  };

  const handleSuspend = async () => {
    const organizerId = selectedOrganizer.organizer_id?._id || selectedOrganizer.organizer_id;
    if (!organizerId) {
      toast.error("Organizer record not found — application may not have been approved yet");
      return;
    }
    if (!suspendReason.trim()) {
      toast.error("Please provide a suspension reason");
      return;
    }
    try {
      await dispatch(
        suspendOrganizer({
          organizerId,
          reason: suspendReason,
          duration_days: suspendDuration ? parseInt(suspendDuration) : undefined,
        })
      ).unwrap();
      toast.success("Organizer suspended successfully");
      setShowSuspendModal(false);
      setSuspendReason("");
      setSuspendDuration("");
      fetchList(organizersPage);
    } catch (error) {
      toast.error(error.message || "Failed to suspend user");
    }
  };

  const handleUnsuspend = async () => {
    const organizerId = selectedOrganizer.organizer_id?._id || selectedOrganizer.organizer_id;
    if (!organizerId) {
      toast.error("Organizer record not found");
      return;
    }
    try {
      await dispatch(unsuspendOrganizer(organizerId)).unwrap();
      toast.success("User unsuspended successfully");
      setShowUnsuspendConfirm(false);
      fetchList(organizersPage);
    } catch (error) {
      toast.error(error.message || "Failed to unsuspend user");
    }
  };

  // ── Helpers ───────────────────────────────────────────────────

  const getStatusBadge = (status) => {
    const config = {
      pending: { tone: "info", label: "Pending", icon: <Clock className="w-3 h-3" /> },
      approved: { tone: "success", label: "Approved", icon: <CheckCircle className="w-3 h-3" /> },
      rejected: { tone: "danger", label: "Rejected", icon: <XCircle className="w-3 h-3" /> },
      suspended: { tone: "warning", label: "Suspended", icon: <Ban className="w-3 h-3" /> },
    };
    const c = config[status] || config.pending;
    return (
      <Badge tone={c.tone}>
        <span className="flex items-center gap-1">{c.icon} {c.label}</span>
      </Badge>
    );
  };

  const openDetails = (organizer) => {
    setSelectedOrganizer(organizer);
    setShowDetailsModal(true);
    setShowEditSection(false);
    setOrganizerEvents([]);
    setActiveTab("details");
  };

  const openApproveConfirm = (organizer) => {
    setSelectedOrganizer(organizer);
    setShowApproveConfirm(true);
  };

  const openRejectModal = (organizer) => {
    setSelectedOrganizer(organizer);
    setShowRejectModal(true);
    setRejectionReason("");
  };

  // ── Edit Override Handlers ─────────────────────────────────────

  const loadOrganizerEvents = async (organizerId) => {
    setLoadingEvents(true);
    try {
      const result = await dispatch(fetchOrganizerEventsForAdmin(organizerId)).unwrap();
      setOrganizerEvents(result?.data?.events || []);
    } catch (error) {
      toast.error("Failed to load organizer events");
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleToggleEditOverride = async (event) => {
    if (!selectedOrganizer?.organizer_id) return;
    setTogglingEventId(event._id);
    try {
      if (event.edit_override?.granted) {
        await dispatch(revokeEditOverride({
          organizerId: selectedOrganizer.organizer_id,
          eventId: event._id,
        })).unwrap();
        toast.success("Edit access revoked");
      } else {
        await dispatch(grantEditOverride({
          organizerId: selectedOrganizer.organizer_id,
          eventId: event._id,
        })).unwrap();
        toast.success("Edit access granted");
      }
      // Refresh events list
      await loadOrganizerEvents(selectedOrganizer.organizer_id);
    } catch (error) {
      toast.error(error.message || "Failed to update edit access");
    } finally {
      setTogglingEventId(null);
    }
  };

  const handleToggleEditSection = () => {
    const newState = !showEditSection;
    setShowEditSection(newState);
    if (newState && organizerEvents.length === 0 && selectedOrganizer?.organizer_id) {
      loadOrganizerEvents(selectedOrganizer.organizer_id);
    }
  };

  const getEventStatusBadge = (status) => {
    const config = {
      submitted: { tone: "info", label: "Submitted" },
      sponsor_listed: { tone: "info", label: "Sponsor Listed" },
      published: { tone: "success", label: "Published" },
      archived: { tone: "danger", label: "Archived" },
    };
    const c = config[status] || config.submitted;
    return <Badge tone={c.tone} size="sm">{c.label}</Badge>;
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Filters Panel */}
      <FilterPanel
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search Section */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Organizers
            </label>
            <div className="flex gap-2">
              <InlineDropdown
                options={SEARCH_FIELDS}
                value={filters.searchField}
                onChange={(e) =>
                  setFilters({ ...filters, searchField: e.target.value })
                }
                className="w-1/3"
              />
              <input
                type="text"
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                placeholder="Enter search term..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <InlineDropdown
              options={STATUS_OPTIONS}
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            />
          </div>
        </div>
      </FilterPanel>

      {/* Sorting Panel */}
      <FilterPanel
        title="Sorting"
        icon={ArrowUpDown}
        hasActiveFilters={sorting.field !== "recent"}
        onClearFilters={() => setSorting({ field: "recent", order: "desc" })}
        defaultOpen={false}
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
          {SORT_OPTIONS.map((opt) => (
            <div key={opt.value}>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {opt.label}
              </label>
              <button
                onClick={() => handleSortToggle(opt.value)}
                className={`w-full px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                  sorting.field === opt.value
                    ? "bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-400"
                    : "bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
                } hover:shadow-sm`}
              >
                {sorting.field === opt.value
                  ? `${sorting.order === "desc" ? "↓" : "↑"}`
                  : "Not Sorted"}
              </button>
            </div>
          ))}
        </div>
      </FilterPanel>

      {/* Results Count */}
      <div className="flex justify-between items-center px-1">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {isLoadingOrganizers ? (
            "Loading organizers..."
          ) : (
            <>Showing {organizerApplications.length} organizers</>
          )}
        </div>
      </div>

      {/* Table */}
      {isLoadingOrganizers && organizerApplications.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
        </div>
      ) : (
        <Table
          columns={[
            { key: "organization", title: "Organization" },
            { key: "contact", title: "Contact" },
            { key: "location", title: "Location" },
            { key: "status", title: "Status" },
            { key: "date", title: "Date" },
            { key: "actions", title: "Actions" },
          ]}
          data={organizerApplications}
          renderRow={(org) => (
            <tr
              key={org._id}
              className="hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
            >
              <td className="px-4 py-3">
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {org.organization_name}
                </p>
                <p className="text-sm text-gray-500">
                  {org.registration_type?.replace(/_/g, " ")}
                </p>
              </td>
              <td className="px-4 py-3">
                <p className="text-sm text-gray-800 dark:text-gray-100">{org.applicant_email}</p>
                <p className="text-sm text-gray-500">{org.applicant_phone}</p>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                {org.city}
              </td>
              <td className="px-4 py-3">
                {getStatusBadge(org.organizer_id?.is_active === false ? "suspended" : org.status)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {new Date(org.created_at).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => openDetails(org)}
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 text-xs"
                  >
                    View
                  </Button>
                </div>
              </td>
            </tr>
          )}
        />
      )}

      {/* Empty State */}
      {!isLoadingOrganizers && organizerApplications.length === 0 && (
        <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            No organizers found matching your criteria.
          </p>
        </div>
      )}

      <Pagination
        page={organizersPage}
        totalPages={organizersTotalPages}
        onChange={handlePageChange}
      />

      {/* ── Modals ─────────────────────────────────────────────── */}

      {/* Details Modal */}
      <Modal
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Organizer Details"
      >
        {selectedOrganizer && (
          <>
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === "details"
                    ? "text-green-600 border-b-2 border-green-600"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
                onClick={() => setActiveTab("details")}
              >
                Details
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === "history"
                    ? "text-green-600 border-b-2 border-green-600"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
                onClick={() => setActiveTab("history")}
              >
                Audit History
              </button>
            </div>
          
            {activeTab === "details" ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Organization</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedOrganizer.organization_name}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Registration Type</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedOrganizer.registration_type?.replace(/_/g, ' ')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedOrganizer.applicant_email}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedOrganizer.applicant_phone}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">City</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedOrganizer.city}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                {getStatusBadge(selectedOrganizer.organizer_id?.is_active === false ? "suspended" : selectedOrganizer.status)}
              </div>
            </div>

            {selectedOrganizer.shareholder_profession && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-1">Shareholder Profession</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedOrganizer.shareholder_profession}
                </p>
              </div>
            )}

            {selectedOrganizer.team_members && selectedOrganizer.team_members.length > 0 && (
              <div className="mt-4">
                 <p className="text-sm text-gray-500 mb-2">Team Members</p>
                 <div className="grid gap-3">
                   {selectedOrganizer.team_members.map((member, idx) => (
                     <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-100 dark:border-gray-700">
                       <div className="flex justify-between items-start">
                         <p className="font-medium text-gray-800 dark:text-gray-100">{member.name}</p>
                         <span className="text-xs text-gray-500 bg-white dark:bg-gray-800 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600">
                           {member.experience || 'N/A'} exp
                         </span>
                       </div>
                       <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{member.phone}</p>
                       {member.about && (
                         <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 italic line-clamp-2">
                           "{member.about}"
                         </p>
                       )}
                     </div>
                   ))}
                 </div>
              </div>
            )}

            {selectedOrganizer.cycling_history && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Cycling History</p>
                <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg">
                  {selectedOrganizer.cycling_history}
                </p>
              </div>
            )}

            {selectedOrganizer.rejection_reason && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">
                  Rejection Reason
                </p>
                <p className="text-red-600 dark:text-red-300">
                  {selectedOrganizer.rejection_reason}
                </p>
              </div>
            )}

            {selectedOrganizer.organizer_id?.is_active === false && selectedOrganizer.organizer_id?.suspension && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/30 text-sm mt-4">
                <div className="font-bold text-red-800 dark:text-red-400 flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4" /> Suspended
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-2">"{selectedOrganizer.organizer_id.suspension.reason}"</p>
                {selectedOrganizer.organizer_id.suspension.suspended_until && (
                  <div className="text-xs text-gray-500">
                    Until: {new Date(selectedOrganizer.organizer_id.suspension.suspended_until).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}

            {selectedOrganizer.status === "pending" && (
              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  onClick={() => {
                    setShowDetailsModal(false);
                    openApproveConfirm(selectedOrganizer);
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDetailsModal(false);
                    openRejectModal(selectedOrganizer);
                  }}
                  className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            )}

            {selectedOrganizer.status === "approved" && (
              <>
                {/* Event Edit Permissions Section */}
                {selectedOrganizer.organizer_id && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleToggleEditSection}
                      className="w-full flex items-center justify-between py-2 text-left"
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                        <Pencil className="w-4 h-4 text-green-600" />
                        Event Edit Permissions
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform ${showEditSection ? "rotate-180" : ""}`}
                      />
                    </button>

                    {showEditSection && (
                      <div className="mt-3 space-y-2">
                        {loadingEvents ? (
                          <div className="flex items-center justify-center py-6">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                          </div>
                        ) : organizerEvents.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                            No events found for this organizer.
                          </p>
                        ) : (
                          organizerEvents.map((event) => (
                            <div
                              key={event._id}
                              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-100 dark:border-gray-700"
                            >
                              <div className="flex-1 min-w-0 mr-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                    {event.title}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {getEventStatusBadge(event.status)}
                                  {event.edit_override?.granted && (
                                    <Badge tone="success" size="sm">
                                      <span className="flex items-center gap-1">
                                        <ShieldCheck className="w-3 h-3" />
                                        Edit Granted
                                      </span>
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {event.status !== "archived" && (
                                <button
                                  onClick={() => handleToggleEditOverride(event)}
                                  disabled={togglingEventId === event._id}
                                  className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                                    event.edit_override?.granted
                                      ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40"
                                      : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40"
                                  } ${togglingEventId === event._id ? "opacity-50 cursor-not-allowed" : ""}`}
                                >
                                  {togglingEventId === event._id ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                                  ) : event.edit_override?.granted ? (
                                    <><ShieldOff className="w-3.5 h-3.5" /> Revoke</>                                    
                                  ) : (
                                    <><ShieldCheck className="w-3.5 h-3.5" /> Grant Edit</>                                    
                                  )}
                                </button>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {selectedOrganizer.organizer_id?.is_active === false ? (
                       <Button
                        variant="outline"
                        onClick={() => setShowUnsuspendConfirm(true)}
                        className="flex-1 border-yellow-200 text-yellow-600 hover:bg-yellow-50"
                       >
                         <Unlock className="w-4 h-4 mr-2" />
                         Unsuspend Organizer
                       </Button>
                    ) : (
                      <Button
                        variant="warning"
                        onClick={() => setShowSuspendModal(true)}
                        className="flex-1 shadow-sm"
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Suspend Organizer
                      </Button>
                    )}
                </div>
              </>
            )}
          </div>
            ) : (
              <div className="p-4">
                <EntityAuditHistory entityId={selectedOrganizer._id} entityType="Organizer" />
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Suspend Modal */}
      <Modal
        open={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        title="Suspend User"
      >
        <div className="space-y-4">
           <p className="text-gray-600 dark:text-gray-400">
             Suspend access for "{selectedOrganizer?.organization_name}"? This will prevent the user from logging in.
           </p>
           <TextArea
              label="Suspension Reason *"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Reason for suspension..."
              rows={3}
           />
           <TextInput
              label="Duration (Days)"
              type="number"
              value={suspendDuration}
              onChange={(e) => setSuspendDuration(e.target.value)}
              placeholder="Optional (leave empty for indefinite)"
              min="1"
           />
           <div className="flex gap-3">
             <Button
               variant="outline"
               onClick={() => setShowSuspendModal(false)}
               className="flex-1"
             >
               Cancel
             </Button>
             <Button
               onClick={handleSuspend}
               loading={isUpdating}
               className="flex-1 bg-red-600 hover:bg-red-700"
             >
               Suspend User
             </Button>
           </div>
        </div>
      </Modal>
      
      {/* Unsuspend Confirmation */}
      <ConfirmDialog
        open={showUnsuspendConfirm}
        onClose={() => setShowUnsuspendConfirm(false)}
        onConfirm={handleUnsuspend}
        title="Unsuspend User"
        message={`Are you sure you want to unsuspend "${selectedOrganizer?.organization_name}"? They will regain access immediately.`}
        confirmText="Unsuspend"
        loading={isUpdating}
      />

      {/* Approve Confirmation */}
      <ConfirmDialog
        open={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
        onConfirm={handleApprove}
        title="Approve Organizer"
        message={`Are you sure you want to approve "${selectedOrganizer?.organization_name}"? They will receive login credentials via email.`}
        confirmText="Approve"
        loading={isUpdating}
      />

      {/* Reject Modal */}
      <Modal
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Organizer Application"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Please provide a reason for rejecting "{selectedOrganizer?.organization_name}".
          </p>
          <TextArea
            label="Rejection Reason *"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Explain why the application is being rejected..."
            rows={4}
          />
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowRejectModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              loading={isUpdating}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Reject Application
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizerApplicationsList;
